import { useState, useEffect } from 'react';
import config from '../config/environment';

export interface TemplateField {
  id: string;
  fieldName: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  textAlign: 'left' | 'center' | 'right';
  color: string;
}

export interface TemplateData {
  id: string;
  name: string;
  width: number;
  height: number;
  backgroundColor: string;
  fields: TemplateField[];
}

export const useTemplate = (templateFileId?: string, schemaId?: string) => {
  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!templateFileId || !schemaId) {
      setTemplate(null);
      return;
    }

    const fetchTemplate = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('jwt_token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        // Fetch template configuration and field mappings
        const response = await fetch(
          `${config.REACT_APP_API_BASE_URL}/api/templates/schemas/${schemaId}/complete`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch template: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Transform the API response to match our expected format
        const transformedTemplate: TemplateData = {
          id: templateFileId,
          name: data.schema_name || 'Credential Template',
          width: data.template_width || 800,
          height: data.template_height || 600,
          backgroundColor: data.template_background_color || '#ffffff',
          fields: (data.field_mappings || []).map((field: any) => ({
            id: field.field_name,
            fieldName: field.field_name,
            x: field.x_position,
            y: field.y_position,
            width: field.width,
            height: field.height,
            fontSize: field.font_size,
            fontWeight: (field.font_weight === 'bold' ? 'bold' : 'normal') as 'normal' | 'bold',
            textAlign: (field.text_align === 'center' ? 'center' : field.text_align === 'right' ? 'right' : 'left') as 'left' | 'center' | 'right',
            color: field.color
          }))
        };

        setTemplate(transformedTemplate);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch template';
        setError(errorMessage);
        console.error('Error fetching template:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [templateFileId, schemaId]);

  return { template, loading, error };
}; 